import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  downloadIcon,
  eyeIcon,
  filterIcon,
  receiptLongIcon,
} from "../../assets/icons/SvgIconList";
import ActionIcon from "../../components/purchase/ActionIcon";
import UseOrderDocumentDownloadHook from "../../hooks/UseOrderDocumentDownloadHook";
import StatusChip from "../../components/purchase/StatusChip";
import { ICDatePicker, ICDropDown } from "../../core-component/form";
import { AdvanceSearch, ICButton, ICTable } from "../../core-component/ui";
import { IOption, IPagination } from "../../data/AppInterface";
import UseEventCallbackHook from "../../hooks/UseEventCallbackHook";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { IPaymentOrderFilter } from "../../services/payment-order/PaymentOrderService";
import { ORDER_BY } from "../../utils/AppConstants";
import { getLocalDate } from "../../utils/AppFunctions";
import {
  getCreditTypeOptions,
  getOrderKindOptions,
  getOrderStatusOptions,
  ORDER_STATUS_CHIP_CLASS,
  ORDER_STATUS_LABEL_KEY,
} from "../../utils/PurchaseConstants";
import {
  DOCUMENT_TYPE_ENUM,
  ORDER_DISPLAY_STATUS_ENUM,
} from "../../utils/PurchaseEnumerations";
import { getFirmNameDdl } from "../firm-purchased-subscription/FirmPurchasedSubscriptionViewController";
import PaymentOrderDetail from "./PaymentOrderDetail";
import {
  getAllPaymentOrder,
  getOrderRowClass,
  getPaymentOrderFields,
  IPaymentOrderRow,
  PAYMENT_ORDER_FILTER_INITIAL_VALUE,
} from "./PaymentTransactionViewController";

let timer: ReturnType<typeof setTimeout>;

interface IRazorpayOrderCellProps {
  row: IPaymentOrderRow;
}

const RazorpayOrderCell: React.FC<IRazorpayOrderCellProps> = ({ row }) => (
  <span className="break-all">{row.gateway_order_id ?? "-"}</span>
);

const PaymentTransactionView = () => {
  const { t } = UseTranslationHook();
  const { download, isBusy } = UseOrderDocumentDownloadHook();
  const location = useLocation();
  const navigate = useNavigate();
  const firmIdFromLink: string | undefined = location?.state?.id;
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { pagination, setChangePagination } = UsePaginationHook();
  const [list, setList] = useState<IPaymentOrderRow[]>([]);
  const [firmOptions, setFirmOptions] = useState<IOption<string>[]>([]);
  const [orderToView, setOrderToView] = useState<string>();
  const [filter, setFilter] = useState<IPaymentOrderFilter>(() => ({
    ...PAYMENT_ORDER_FILTER_INITIAL_VALUE,
    ...(firmIdFromLink
      ? { firm_id: firmIdFromLink, status: ORDER_DISPLAY_STATUS_ENUM.All }
      : {}),
  }));

  useEffect(() => {
    if (firmIdFromLink) navigate(location.pathname, { replace: true });
  }, [firmIdFromLink, location.pathname, navigate]);

  useEffect(() => {
    getFirmNameDdl(setFirmOptions);
  }, []);

  const fetchList = (paginationPayload: IPagination) => {
    setIsLoading(true);
    if (timer) clearTimeout(timer);
    timer = setTimeout(async () => {
      await getAllPaymentOrder(setList, paginationPayload, setChangePagination, search, filter);
      setIsLoading(false);
    }, 500);
  };

  const fetchFirstPage = UseEventCallbackHook(() => fetchList({ ...pagination, currentPage: 1 }));

  useEffect(() => {
    fetchFirstPage();
  }, [search, filter, fetchFirstPage]);

  const statusCell = (row: IPaymentOrderRow) => (
    <StatusChip
      label={t(ORDER_STATUS_LABEL_KEY[row.display_status])}
      className={ORDER_STATUS_CHIP_CLASS[row.display_status]}
    />
  );

  const customActionView = (row: IPaymentOrderRow) => (
    <div className="flex items-center justify-center w-full gap-2">
      <ActionIcon
        icon={eyeIcon}
        title={t("view_tooltip_icon")}
        onClick={() => setOrderToView(row.id)}
      />
      {row.display_status === ORDER_DISPLAY_STATUS_ENUM.Paid && (
        <>
          <ActionIcon
            icon={downloadIcon}
            title={t("download_invoice")}
            onClick={() => download(row.id, DOCUMENT_TYPE_ENUM.Invoice)}
            busy={isBusy(row.id, DOCUMENT_TYPE_ENUM.Invoice)}
            className="text-accent"
          />
          <ActionIcon
            icon={receiptLongIcon}
            title={t("download_receipt")}
            onClick={() => download(row.id, DOCUMENT_TYPE_ENUM.Receipt)}
            busy={isBusy(row.id, DOCUMENT_TYPE_ENUM.Receipt)}
            className="text-accent"
          />
        </>
      )}
    </div>
  );

  const setFilterValue = (key: keyof IPaymentOrderFilter, value: string | null) =>
    setFilter((previous) => ({ ...previous, [key]: value }));

  const filterView = (
    <div className="w-full grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
      <ICDropDown
        placeholder={t("status")}
        selected={filter.status ?? undefined}
        options={getOrderStatusOptions(t)}
        onSelect={(option) => setFilterValue("status", option.data_value)}
      />
      <ICDropDown
        placeholder={t("kind")}
        selected={filter.order_kind ?? undefined}
        options={getOrderKindOptions(t)}
        onSelect={(option) => setFilterValue("order_kind", option.data_value)}
      />
      <ICDropDown
        placeholder={t("credit_type")}
        selected={filter.credit_type ?? undefined}
        options={getCreditTypeOptions(t)}
        onSelect={(option) => setFilterValue("credit_type", option.data_value)}
      />
      <ICDropDown
        placeholder={t("firm_name")}
        selected={filter.firm_id ?? undefined}
        options={firmOptions}
        searchable
        onSelect={(option) => setFilterValue("firm_id", option.data_value)}
      />
      <ICDatePicker
        useValue
        label={t("from_date")}
        placeholder={t("from_date")}
        value={filter.from_date ? getLocalDate(filter.from_date) : undefined}
        onChangeDate={(event) =>
          setFilterValue("from_date", event.target.value || null)
        }
      />
      <ICDatePicker
        useValue
        label={t("to_date")}
        placeholder={t("to_date")}
        value={filter.to_date ? getLocalDate(filter.to_date) : undefined}
        onChangeDate={(event) =>
          setFilterValue("to_date", event.target.value || null)
        }
      />
      <ICButton
        type="button"
        onClick={() => setFilter(PAYMENT_ORDER_FILTER_INITIAL_VALUE)}
        className="flex !w-fit"
      >
        <div className="size-5 grid place-self-center">{filterIcon}</div>
        &nbsp;{t("clear")}
      </ICButton>
    </div>
  );

  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleSearchChange={setSearch}
        searchText={search}
        filterNode={filterView}
        defaultOpen={!!firmIdFromLink}
        refreshTable={() => fetchList(pagination)}
        title={t("payment_transaction")}
      />
      <ICTable<IPaymentOrderRow>
        isLoading={isLoading}
        totalFlex="grid grid-cols-21"
        fields={getPaymentOrderFields(
          {
            action: customActionView,
            status: statusCell,
            razorpayOrder: (row) => <RazorpayOrderCell row={row} />,
          },
          t,
        )}
        data={list}
        pagination={pagination}
        rowClassName={getOrderRowClass}
        handleChangePerPageRows={(perPageRows) => fetchList({ ...pagination, perPageRows, currentPage: 1 })}
        handleChangePage={(page) => fetchList({ ...pagination, currentPage: page })}
        handleSort={(field) =>
          fetchList({
            ...pagination,
            sortBy: field.key,
            orderBy: pagination.orderBy === ORDER_BY.desc ? ORDER_BY.asc : ORDER_BY.desc,
          })
        }
      />
      {orderToView && (
        <PaymentOrderDetail orderId={orderToView} handleClose={() => setOrderToView(undefined)} />
      )}
    </div>
  );
};

export default PaymentTransactionView;
